// SOURCES: what's loaded, drop zone with column mapping, Monday pull, build the radar, link Claude Code, activity, erase.
import type { SourceKind } from "../../shared/network";
import { FIELDS, KIND_LABEL } from "../../shared/sources";
import type { BuildLogLine, ConferenceLead } from "../../shared/ingest";
import { buildXlsx } from "../lib/xlsx-write";
import type { ActivityRow, MondayBoard, MondayItem, SourceMeta, TokenInfo } from "../lib/api";
import { buildRadar } from "../sources/build";
import { detect, mondayToParsed, normalize, readFile, sampleRows, sourceLabel, withKind, withMapping, type Detection, type ParsedFile } from "../sources/detect";
import type { MountFn } from "../context";
import { h, mount, num, tick } from "../ui/dom";
import { field, key, lamp, panel } from "../ui/widgets";

const KINDS = Object.keys(KIND_LABEL) as SourceKind[];
const XLSX = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

interface Pending {
  id: string;
  pf?: ParsedFile;
  det?: Detection;
  filename: string;
  label: string;
  labelEdited: boolean;
  event: string;
  date: string;
  origin: "file" | "monday";
  error?: string;
  busy?: boolean;
}

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const stamp = (iso: string) => (iso ? iso.slice(5, 10) + " " + new Date(iso).toTimeString().slice(0, 5) : "");

export const mountSources: MountFn = (el, ctx) => {
  const api = ctx.api;
  let sources: SourceMeta[] = [];
  const pending: Pending[] = [];
  let building = false;
  let buildLog: (BuildLogLine | { tag: string; text: string; tone: "err" })[] = [];
  let leads: ConferenceLead[] = [];
  let removeArm: string | null = null;
  let tokens: TokenInfo[] = [];
  let newToken: { token: string; label: string } | null = null;
  let tokenLabel = "Claude Code";
  let activity: ActivityRow[] = [];
  let eraseText = "";
  // Monday
  let mAccount = ctx.session.monday?.account ?? "";
  let mConnected = !!ctx.session.monday?.connected;
  let boards: MondayBoard[] = [];
  let boardId = "";
  let mProgress = "";
  let mBusy = false;
  let mError = "";
  let mToken = "";
  let alive = true;

  const sessionChanged = () => document.dispatchEvent(new CustomEvent("radar:session"));

  const host = {
    pre: h("div"), build: h("div"), drop: h("div"), cards: h("div"), stored: h("div"), monday: h("div"), claude: h("div"), activity: h("div"), erase: h("div"),
  };
  mount(
    el,
    h("div", { class: "pagehead" }, h("h1", { class: "title" }, "Sources"), h("span", { class: "meta" }, "WHAT THE RADAR IS BUILT FROM")),
    h("div", { class: "twocol", style: "margin-bottom:14px" }, host.pre, host.build),
    host.drop,
    host.cards,
    host.stored,
    h("div", { class: "twocol", style: "margin-bottom:14px" }, host.monday, host.claude),
    h("div", { class: "twocol" }, host.activity, host.erase),
  );

  /* ---------- preflight ---------- */
  const kindRows = (k: SourceKind) => sources.filter((s) => s.kind === k);
  const rowsOf = (k: SourceKind) => kindRows(k).reduce((n, s) => n + s.rows, 0);
  const canBuild = () => rowsOf("crm") > 0 || rowsOf("contacts") > 0;

  function paintPre() {
    const items: { kind: SourceKind; name: string }[] = [
      { kind: "crm", name: "CRM board (Monday)" }, { kind: "contacts", name: "Google Contacts" }, { kind: "linkedin", name: "LinkedIn connections" },
      { kind: "conference", name: "Conference lists" }, { kind: "touches", name: "Touch log" }, { kind: "deals", name: "Past deals" }, { kind: "participation", name: "Who invested (participation)" },
    ];
    mount(
      host.pre,
      panel(
        "PREFLIGHT",
        h(
          "ul",
          { class: "checklist" },
          items.map((it) => {
            const n = rowsOf(it.kind);
            const files = kindRows(it.kind).length;
            const missing = it.kind === "crm" && n === 0 && rowsOf("contacts") === 0;
            const req = it.kind === "crm" ? "REQUIRED*" : "";
            return h(
              "li",
              null,
              n > 0 ? lamp("LOADED", true, "green") : missing ? lamp("MISSING", true, "red") : lamp("OPTIONAL", false),
              h("div", null, it.name, req ? h("span", { class: "micro", style: "margin-left:8px" }, req) : null),
              h("span", { class: "num " + (n ? "" : "muted"), style: "font-size:12px" }, n ? `${num(n)} ROWS · ${files} FILE${files > 1 ? "S" : ""}` : missing ? "NEEDS THIS OR GOOGLE CONTACTS" : "—"),
            );
          }),
        ),
      ),
    );
  }

  /* ---------- build ---------- */
  function paintBuild() {
    const net = ctx.network;
    const log = h("div", { class: "log", "aria-live": "polite" });
    if (!buildLog.length) log.appendChild(h("div", { class: "muted" }, building ? "BUILDING…" : net ? "READY. Build again after adding or removing sources." : "NOTHING BUILT YET."));
    for (const l of buildLog) log.appendChild(h("div", { class: "r" }, h("span", { class: "tag" }, l.tag), h("span", { class: l.tone === "ok" ? "ok" : l.tone === "warn" ? "warn" : l.tone === "err" ? "err" : "" }, l.text)));
    const exportLeads = () => {
      const rows = [["Name", "Company", "Title", "Email", "Location", "Registration", "Event", "Date"], ...leads.map((l) => [l.name, l.company, l.title, l.email, l.location, l.registration, l.event, l.date])];
      ctx.save(`conference-leads-${ctx.asOf}.xlsx`, new Blob([buildXlsx([{ name: "Leads", rows }]) as BlobPart], { type: XLSX }));
    };
    mount(
      host.build,
      panel(
        "BUILD RADAR",
        [
          h("p", { class: "help", style: "margin-top:0" }, net ? `LOADED: ${num(net.contacts.length)} PEOPLE, AS OF ${net.generated}.` : "No radar yet. Add a CRM or Google source, then build."),
          h("div", { class: "row", style: "margin-bottom:10px" }, key(building ? "BUILDING" : "BUILD RADAR", () => void doBuild(), { primary: true, disabled: building || !canBuild() }), !canBuild() ? h("span", { class: "micro" }, "NEEDS A CRM OR GOOGLE SOURCE") : null),
          h("div", { class: "micro", style: "margin-bottom:4px" }, "BUILD LOG"),
          log,
          leads.length ? h("div", { class: "row", style: "margin-top:12px" }, h("span", { class: "mono", style: "font-size:12.5px" }, `${num(leads.length)} conference names not in your network`), h("span", { class: "grow" }), key("EXPORT XLSX", exportLeads, { sm: true })) : null,
        ],
      ),
    );
  }

  async function doBuild() {
    building = true;
    buildLog = [];
    leads = [];
    paintBuild();
    await tick();
    try {
      const stored = await Promise.all(sources.map((s) => api.getSource(s.id)));
      await tick();
      const result = buildRadar(stored, ctx.asOf);
      buildLog = [...result.log];
      leads = result.leads;
      paintBuild();
      await api.putNetwork(result.network, result.slim);
      await ctx.setNetwork(result.network);
      buildLog = [...buildLog, { tag: "VAULT", text: `Saved ${num(result.network.contacts.length)} people.`, tone: "ok" }];
      ctx.toast("Radar built.");
    } catch (e) {
      buildLog = [...buildLog, { tag: "FAULT", text: errText(e), tone: "err" }];
    }
    building = false;
    if (alive) paintBuild();
  }

  /* ---------- drop zone and mapping cards ---------- */
  const fileInput = h("input", { type: "file", multiple: true, accept: ".xlsx,.csv,.tsv,.txt", class: "hidden", "aria-label": "Choose files" });
  fileInput.addEventListener("change", () => {
    void addFiles([...(fileInput.files ?? [])]);
    fileInput.value = "";
  });
  const dz = h("div", { class: "drop", tabIndex: 0, role: "button" }, "DROP FILES OR CLICK TO CHOOSE", h("div", { class: "micro", style: "margin-top:8px" }, ".XLSX  .CSV  .TSV  .TXT"));
  dz.addEventListener("click", () => fileInput.click());
  dz.addEventListener("keydown", (e) => (e.key === "Enter" || e.key === " " ? (e.preventDefault(), fileInput.click()) : null));
  dz.addEventListener("dragover", (e) => (e.preventDefault(), dz.classList.add("over")));
  dz.addEventListener("dragleave", () => dz.classList.remove("over"));
  dz.addEventListener("drop", (e) => {
    e.preventDefault();
    dz.classList.remove("over");
    void addFiles([...(e.dataTransfer?.files ?? [])]);
  });
  mount(host.drop, dz, fileInput);

  async function addFiles(files: File[]) {
    for (const f of files) {
      const p: Pending = { id: crypto.randomUUID(), filename: f.name, label: f.name, labelEdited: false, event: "", date: "", origin: "file" };
      pending.push(p);
      paintCards();
      try {
        p.pf = await readFile(f);
        initDet(p, detect(p.pf));
      } catch (e) {
        p.error = errText(e);
      }
      paintCards();
    }
  }
  function initDet(p: Pending, det: Detection) {
    p.det = det;
    if (!p.labelEdited) p.label = sourceLabel(det, p.filename);
    if (det.kind === "conference") {
      p.event = det.event?.name || p.event || p.filename.replace(/\.[^.]+$/, "");
      p.date = det.event?.date || p.date;
    }
  }

  function card(p: Pending): HTMLElement {
    const dismiss = key("DISMISS", () => (pending.splice(pending.indexOf(p), 1), paintCards()), { sm: true });
    if (p.error || !p.pf || !p.det) {
      return h("div", { class: "map-card" }, h("div", { class: "mh" }, h("span", { class: "fnm" }, p.filename), p.error ? h("span", { class: "rd mono", style: "font-size:12px" }, p.error) : h("span", { class: "muted mono" }, "READING…"), h("span", { class: "grow", style: "flex:1" }), p.error ? dismiss : null));
    }
    const { pf, det } = p;
    const kindSel = h("select", { "aria-label": "Kind of file" }, KINDS.map((k) => h("option", { value: k, selected: k === det.kind }, KIND_LABEL[k])));
    kindSel.addEventListener("change", () => {
      initDet(p, withKind(pf, det, kindSel.value as SourceKind));
      paintCards();
    });
    const samples = sampleRows(pf, det, 3);
    const mapRows = FIELDS[det.kind].map((f) => {
      const cur = det.mapping[f.key];
      const s = h("select", { "aria-label": f.label }, h("option", { value: "" }, "—"), det.headers.map((hd, i) => h("option", { value: String(i), selected: cur === i }, hd || `(column ${i + 1})`)));
      s.addEventListener("change", () => {
        p.det = withMapping(pf, det, { ...det.mapping, [f.key]: s.value === "" ? null : Number(s.value) });
        paintCards();
      });
      const vals = cur == null ? [] : samples.map((r) => r[cur]).filter(Boolean);
      return h("tr", null, h("td", { style: "white-space:nowrap;padding-right:14px" }, f.label), h("td", { style: "min-width:150px" }, s), h("td", { class: "s", title: vals.join(" | ") }, vals.join("  |  ")));
    });
    const labelIn = h("input", { type: "text", value: p.label, "aria-label": "Label" });
    labelIn.addEventListener("input", () => ((p.label = labelIn.value), (p.labelEdited = true)));
    const isConf = det.kind === "conference";
    const evIn = h("input", { type: "text", value: p.event, "aria-label": "Event name" });
    evIn.addEventListener("input", () => (p.event = evIn.value));
    const dateIn = h("input", { type: "date", value: p.date, "aria-label": "Event date" });
    dateIn.addEventListener("input", () => {
      p.date = dateIn.value;
      store.disabled = !!det.missing.length || (isConf && !p.date) || !!p.busy;
    });
    const store = key(p.busy ? "STORING…" : "STORE", () => void doStore(p), { primary: true, disabled: det.missing.length > 0 || (isConf && !p.date) || !!p.busy });
    return h(
      "div",
      { class: "map-card" },
      h(
        "div",
        { class: "mh" },
        h("span", { class: "fnm" }, p.filename),
        h("span", { class: "micro" }, p.origin === "monday" ? "MONDAY PULL" : "FILE"),
        h("span", { class: "mono " + (det.confidence < 0.5 ? "am" : "gr"), style: "font-size:12px" }, `CONFIDENCE ${Math.round(det.confidence * 100)}%${det.confidence < 0.5 ? "  CHECK THE KIND" : ""}`),
        h("span", { class: "mono", style: "font-size:12px" }, `${num(det.dataRows)} DATA ROWS`),
        h("span", { class: "grow", style: "flex:1" }),
        dismiss,
      ),
      h(
        "div",
        { class: "mb" },
        h("div", { class: "formgrid", style: "margin-bottom:14px" }, field("KIND", kindSel), field("LABEL", labelIn), isConf ? field("EVENT NAME", evIn) : null, isConf ? field("EVENT DATE (REQUIRED)", dateIn) : null),
        det.missing.length ? h("p", { class: "rd mono", style: "font-size:12.5px;margin:0 0 10px" }, "MISSING: " + det.missing.join(", ") + ". Pick the column below.") : null,
        h("div", { style: "overflow-x:auto" }, h("table", null, h("thead", null, h("tr", null, h("th", null, "FIELD"), h("th", null, "COLUMN"), h("th", null, "SAMPLE"))), h("tbody", null, mapRows))),
      ),
      h("div", { class: "foot" }, store, isConf && !p.date ? h("span", { class: "am mono", style: "font-size:12px" }, "ADD THE EVENT DATE") : null),
    );
  }
  function paintCards() {
    mount(host.cards, pending.map(card));
  }

  async function doStore(p: Pending) {
    if (!p.pf || !p.det) return;
    p.busy = true;
    paintCards();
    try {
      const rows = normalize(p.pf, p.det);
      const conf = p.det.kind === "conference";
      const meta: SourceMeta = {
        id: crypto.randomUUID(), kind: p.det.kind, label: p.label.trim() || sourceLabel(p.det, p.filename), filename: p.filename, rows: rows.length,
        uploadedAt: new Date().toISOString(), origin: p.origin, ...(conf ? { event: p.event.trim(), date: p.date } : {}),
      };
      await api.putSource({ meta, rows });
      pending.splice(pending.indexOf(p), 1);
      ctx.toast(`Stored ${num(rows.length)} rows.`);
      await loadSources();
    } catch (e) {
      p.busy = false;
      ctx.toast(errText(e), "err");
    }
    if (alive) paintCards();
  }

  /* ---------- stored sources ---------- */
  async function loadSources() {
    try {
      sources = await api.listSources();
    } catch (e) {
      ctx.toast(errText(e), "err");
    }
    if (!alive) return;
    paintPre();
    paintStored();
    paintBuild();
  }
  function paintStored() {
    const t = sources.length
      ? h(
          "div",
          { class: "table-wrap", style: "border:0" },
          h(
            "table",
            null,
            h("thead", null, h("tr", null, ["KIND", "LABEL", "FILE", "ROWS", "ADDED", "FROM", ""].map((x, i) => h("th", { class: i === 3 ? "num" : "" }, x)))),
            h(
              "tbody",
              null,
              sources.map((s) =>
                h(
                  "tr",
                  null,
                  h("td", null, KIND_LABEL[s.kind]),
                  h("td", { class: "clip" }, s.label),
                  h("td", { class: "clip muted" }, s.filename),
                  h("td", { class: "num" }, num(s.rows)),
                  h("td", { class: "num muted" }, stamp(s.uploadedAt)),
                  h("td", null, h("span", { class: "chip" }, s.origin.toUpperCase())),
                  h("td", { style: "text-align:right" }, key(removeArm === s.id ? "CONFIRM" : "REMOVE", async () => {
                    if (removeArm !== s.id) {
                      removeArm = s.id;
                      paintStored();
                      setTimeout(() => (removeArm === s.id ? ((removeArm = null), alive && paintStored()) : null), 3500);
                      return;
                    }
                    removeArm = null;
                    try {
                      await api.deleteSource(s.id);
                    } catch (e) {
                      ctx.toast(errText(e), "err");
                    }
                    await loadSources();
                  }, { danger: true, sm: true })),
                ),
              ),
            ),
          ),
        )
      : h("p", { class: "muted mono", style: "font-size:12px;margin:0" }, "NOTHING STORED YET.");
    mount(host.stored, panel("STORED SOURCES", t, null, ""));
    host.stored.firstElementChild!.setAttribute("style", "margin-bottom:14px");
  }

  /* ---------- Monday ---------- */
  function paintMonday() {
    const tokenIn = h("input", { type: "password", autocomplete: "off", placeholder: "monday.com API token", value: mToken, "aria-label": "Monday API token" });
    tokenIn.addEventListener("input", () => (mToken = tokenIn.value));
    const connect = key(mBusy ? "WORKING" : "CONNECT", async () => {
      if (!mToken.trim()) return;
      mBusy = true;
      mError = "";
      paintMonday();
      try {
        const r = await api.monday.connect(mToken.trim());
        mAccount = r.account;
        boards = r.boards;
        boardId = boards[0]?.id ?? "";
        mConnected = true;
        mToken = "";
        sessionChanged();
      } catch (e) {
        mError = errText(e);
      }
      mBusy = false;
      if (alive) paintMonday();
    }, { primary: true, disabled: mBusy });
    let body: Node[];
    if (!mConnected) {
      body = [h("div", { class: "row" }, h("div", { class: "grow" }, tokenIn), connect)];
    } else {
      const bs = h("select", { "aria-label": "Board" }, boards.map((b) => h("option", { value: b.id, selected: b.id === boardId }, b.name + (b.items != null ? ` (${num(b.items)})` : ""))));
      bs.addEventListener("change", () => (boardId = bs.value));
      body = [
        h("p", { class: "mono", style: "font-size:12.5px;margin:0 0 10px" }, h("span", { class: "micro" }, "ACCOUNT  "), mAccount),
        h("div", { class: "row" }, h("div", { class: "grow" }, boards.length ? bs : h("span", { class: "muted mono" }, "No boards found.")), key(mBusy ? "PULLING" : "PULL", () => void pull(), { primary: true, disabled: mBusy || !boardId }), key("DISCONNECT", async () => {
          try {
            await api.monday.disconnect();
          } catch (e) {
            ctx.toast(errText(e), "err");
          }
          mConnected = false;
          mAccount = "";
          boards = [];
          mProgress = "";
          sessionChanged();
          paintMonday();
        }, { danger: true })),
      ];
    }
    mount(
      host.monday,
      panel("MONDAY", [
        ...body,
        mProgress ? h("p", { class: "num cy", style: "font-size:12.5px;margin:10px 0 0" }, mProgress) : null,
        mError ? h("p", { class: "rd mono", style: "font-size:12px;margin:10px 0 0" }, mError) : null,
        h("p", { class: "help", style: "margin-bottom:0;margin-top:12px" }, h("b", null, "Where's the token? "), "On monday.com: profile picture, Developers, My access tokens. Or skip this: a Monday ", h("b", null, "Export to Excel"), " file works too. Drop it above."),
      ]),
    );
  }
  async function pull() {
    mBusy = true;
    mError = "";
    mProgress = "PULLED 0";
    paintMonday();
    try {
      const first = await api.monday.items(boardId, null);
      if (first.errors?.length) throw new Error(first.errors[0].message);
      const b = first.data?.boards?.[0];
      if (!b) throw new Error("Monday returned no board.");
      const items: MondayItem[] = [...b.items_page.items];
      let cursor = b.items_page.cursor;
      mProgress = `PULLED ${num(items.length)}`;
      paintMonday();
      while (cursor) {
        const page = await api.monday.items(boardId, cursor);
        if (page.errors?.length) throw new Error(page.errors[0].message);
        const np = page.data?.next_items_page;
        items.push(...(np?.items ?? []));
        cursor = np?.cursor ?? null;
        mProgress = `PULLED ${num(items.length)}`;
        paintMonday();
      }
      const pf = mondayToParsed(b.name, b.columns, items);
      const p: Pending = { id: crypto.randomUUID(), pf, filename: pf.filename, label: pf.filename, labelEdited: false, event: "", date: "", origin: "monday" };
      initDet(p, detect(pf, "crm"));
      pending.push(p);
      paintCards();
      mProgress = `PULLED ${num(items.length)}  ·  CHECK THE MAPPING ABOVE, THEN STORE`;
    } catch (e) {
      mError = errText(e);
    }
    mBusy = false;
    if (alive) paintMonday();
  }

  /* ---------- Claude Code link ---------- */
  async function loadTokens() {
    try {
      tokens = await api.tokens.list();
    } catch (e) {
      ctx.toast(errText(e), "err");
    }
    if (alive) paintClaude();
  }
  function paintClaude() {
    const lb = h("input", { type: "text", value: tokenLabel, "aria-label": "Token label" });
    lb.addEventListener("input", () => (tokenLabel = lb.value));
    const cmd = (t: string) => `claude mcp add --transport http radar ${location.origin}/mcp --header "Authorization: Bearer ${t}"`;
    mount(
      host.claude,
      panel("LINK CLAUDE CODE", [
        h("div", { class: "row" }, h("div", { class: "grow" }, lb), key("CREATE TOKEN", async () => {
          try {
            const r = await api.tokens.create(tokenLabel.trim() || "Claude Code");
            newToken = { token: r.token, label: tokenLabel };
          } catch (e) {
            ctx.toast(errText(e), "err");
          }
          await loadTokens();
        }, { primary: true })),
        newToken
          ? h(
              "div",
              { style: "margin-top:12px" },
              h("div", { class: "micro am", style: "margin-bottom:4px" }, "SHOWN ONCE. COPY IT NOW."),
              h("div", { class: "codebox" }, cmd(newToken.token)),
              h("div", { class: "keys", style: "margin-top:8px" }, key("COPY", async () => {
                try {
                  await navigator.clipboard.writeText(cmd(newToken!.token));
                  ctx.toast("Copied.");
                } catch {
                  ctx.toast("Couldn't copy. Select the text and copy it.", "warn");
                }
              }, { sm: true }), key("DONE", () => ((newToken = null), paintClaude()), { sm: true })),
            )
          : null,
        tokens.length
          ? h("div", { class: "scroll-x", style: "margin-top:12px" }, h("table", { class: "t" }, h("tbody", null, tokens.map((t) => h("tr", null, h("td", null, t.label), h("td", { class: "num muted" }, "MADE " + stamp(t.createdAt)), h("td", { class: "num muted" }, t.lastUsedAt ? "USED " + stamp(t.lastUsedAt) : "NEVER USED"), h("td", { style: "text-align:right" }, key("REVOKE", async () => {
              try {
                await api.tokens.revoke(t.id);
              } catch (e) {
                ctx.toast(errText(e), "err");
              }
              await loadTokens();
            }, { danger: true, sm: true })))))))
          : h("p", { class: "muted mono", style: "font-size:12px;margin:12px 0 0" }, "NO TOKENS."),
      ]),
    );
  }

  /* ---------- activity and erase ---------- */
  async function loadActivity() {
    try {
      activity = await api.activity();
    } catch (e) {
      ctx.toast(errText(e), "err");
    }
    if (alive) paintActivity();
  }
  function paintActivity() {
    mount(
      host.activity,
      panel(
        "ACTIVITY",
        activity.length ? h("div", { style: "max-height:260px;overflow-y:auto" }, h("table", { class: "t" }, h("tbody", null, activity.map((a) => h("tr", null, h("td", { class: "num muted", style: "white-space:nowrap" }, stamp(a.ts)), h("td", null, a.action), h("td", { class: "muted clip" }, a.detail)))))) : h("p", { class: "muted mono", style: "font-size:12px;margin:0" }, "NOTHING YET."),
        key("REFRESH", () => void loadActivity(), { sm: true }),
      ),
    );
  }
  function paintErase() {
    const inp = h("input", { type: "text", placeholder: "Type ERASE", value: eraseText, autocomplete: "off", "aria-label": "Type ERASE to confirm" });
    const go = key("ERASE WORKSPACE", async () => {
      try {
        await api.deleteWorkspace();
        try {
          await api.signOut();
        } catch {
          /* already out */
        }
        location.hash = "";
        location.reload();
      } catch (e) {
        ctx.toast(errText(e), "err");
      }
    }, { danger: true, disabled: eraseText !== "ERASE" });
    inp.addEventListener("input", () => {
      eraseText = inp.value;
      go.disabled = eraseText !== "ERASE";
    });
    mount(host.erase, panel("ERASE WORKSPACE", [h("p", { class: "help", style: "margin-top:0" }, "Deletes every source, the radar, your notes, holds and deals. There is no undo."), h("div", { class: "row" }, h("div", { class: "grow" }, inp), go)], null, ""));
  }

  paintPre();
  paintBuild();
  paintStored();
  paintMonday();
  paintClaude();
  paintActivity();
  paintErase();
  void loadSources();
  void loadTokens();
  void loadActivity();
  if (mConnected)
    void api.monday.boards().then((b) => {
      boards = b;
      boardId ||= b[0]?.id ?? "";
      if (alive) paintMonday();
    }).catch(() => undefined);
  return () => {
    alive = false;
  };
};
